import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/adminApi";
import { AdminHeader } from "@/components/admin/AdminDashboard";

const BLANK = { title: "", trade: "", location: "", pay: "", shift: "", experience: "", per_diem: false, entry_level: false, clearance: "DBIDS Access Readiness" };

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => { setJobs(await adminApi.jobs()); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (j) => { setEditing(j.id); setForm({ ...BLANK, ...j }); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.trade || !form.location || !form.pay) { toast.error("Title, trade, location and pay are required."); return; }
    setBusy(true);
    try {
      const payload = { title: form.title, trade: form.trade, location: form.location, pay: form.pay, shift: form.shift, experience: form.experience, per_diem: !!form.per_diem, entry_level: !!form.entry_level, clearance: form.clearance || "DBIDS Access Readiness" };
      if (editing) await adminApi.updateJob(editing, payload); else await adminApi.createJob(payload);
      toast.success(editing ? "Job updated" : "Job listing added");
      setOpen(false); await load();
    } catch { toast.error("Could not save job."); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    try { await adminApi.deleteJob(delId); toast.success("Job deleted"); setDelId(null); await load(); }
    catch { toast.error("Could not delete job."); }
  };

  return (
    <div>
      <AdminHeader
        eyebrow="MARKETPLACE — JOB LISTINGS"
        title="Job Listings"
        sub={`${jobs.length} live listings shown on the public job board.`}
        right={<Button data-testid="job-add-button" onClick={openNew} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11"><Plus size={16} className="mr-2" /> Add Job</Button>}
      />

      <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-jobs-list">
        {jobs.map((j) => (
          <div key={j.id} className="bg-[#10161F] border border-white/10 p-5" data-testid={`job-row-${j.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-bold uppercase">{j.title}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#FF5E1E] mt-1">{j.trade}</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`job-edit-${j.id}`} onClick={() => openEdit(j)} className="p-2 text-[#8FA3B8] hover:text-white border border-white/10 hover:border-[#FF5E1E]/60 transition-colors"><Pencil size={14} /></button>
                <button data-testid={`job-delete-${j.id}`} onClick={() => setDelId(j.id)} className="p-2 text-[#8FA3B8] hover:text-[#ff5a5a] border border-white/10 hover:border-[#ff5a5a]/60 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#B8C4D0]">
              <span className="flex items-center gap-1"><MapPin size={13} className="text-[#FF5E1E]" /> {j.location}</span>
              <span className="text-[#10B981] font-medium">{j.pay}</span>
              {j.shift && <span className="text-[#8FA3B8]">{j.shift}</span>}
              {j.entry_level && <span className="font-mono-tech text-[9px] tracking-[0.1em] border border-[#FFB800]/40 text-[#FFB800] px-1.5 py-0.5">ENTRY</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#10161F] border-white/15 text-white rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display font-black uppercase">{editing ? "Edit Job" : "Add Job Listing"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <JF label="Title"><Input data-testid="job-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="jf-input" placeholder="Structural Welder" /></JF>
            <JF label="Trade"><Input data-testid="job-form-trade" value={form.trade} onChange={(e) => set("trade", e.target.value)} className="jf-input" placeholder="Welding" /></JF>
            <JF label="Location"><Input data-testid="job-form-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="jf-input" placeholder="Norfolk, VA" /></JF>
            <JF label="Pay"><Input data-testid="job-form-pay" value={form.pay} onChange={(e) => set("pay", e.target.value)} className="jf-input" placeholder="$28–36/hr" /></JF>
            <JF label="Shift"><Input data-testid="job-form-shift" value={form.shift} onChange={(e) => set("shift", e.target.value)} className="jf-input" placeholder="Day / Night" /></JF>
            <JF label="Experience"><Input data-testid="job-form-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} className="jf-input" placeholder="2+ yrs / Entry Level" /></JF>
          </div>
          <div className="flex items-center gap-6 mt-4">
            <label className="flex items-center gap-2 cursor-pointer"><Switch data-testid="job-form-entry" checked={form.entry_level} onCheckedChange={(v) => set("entry_level", v)} /><span className="font-mono-tech text-[10px] tracking-[0.12em] uppercase text-[#8FA3B8]">Entry Level</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><Switch data-testid="job-form-perdiem" checked={form.per_diem} onCheckedChange={(v) => set("per_diem", v)} /><span className="font-mono-tech text-[10px] tracking-[0.12em] uppercase text-[#8FA3B8]">Per Diem</span></label>
          </div>
          <Button data-testid="job-form-save" onClick={save} disabled={busy} className="mt-4 w-full rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11">{busy ? "Saving…" : editing ? "Save Changes" : "Add Listing"}</Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="bg-[#10161F] border-white/15 text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black uppercase">Delete this job?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8FA3B8]">This removes the listing from the public job board. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="job-delete-confirm" onClick={doDelete} className="rounded-none bg-[#ff5a5a] hover:bg-[#ff7373] text-[#0B0F14] font-display font-bold uppercase">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`.jf-input{background:#0B0F14;border:1px solid rgba(255,255,255,0.14);border-radius:0;color:#F4F6F8;}.jf-input:focus-visible{outline:none;border-color:#FF5E1E;box-shadow:0 0 0 1px #FF5E1E;}`}</style>
    </div>
  );
}

function JF({ label, children }) {
  return <div><Label className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase mb-1.5 block">{label}</Label>{children}</div>;
}
